/**
 * DashboardPage — Farm Overview
 *
 * The landing page every user sees after signing in.
 * All data fetches run in parallel via useQueries; each widget degrades
 * gracefully if its endpoint isn't live yet.
 */
import { useQueries } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Leaf, BarChart2, AlertTriangle, CloudSun, Package,
  Sprout, Clock, ChevronRight, RefreshCw, Droplets,
} from 'lucide-react'
import {
  plantsApi, harvestsApi, propagationApi, weatherApi,
  inventoryApi, careApi, type CareEvent,
} from '../lib/api'
import { useAuthStore } from '../lib/store'
import { useAccessibility } from '../lib/accessibility'

// ── Minimal row shapes for the dashboard widgets ────────────────────────────────
// API bodies vary in shape; these cover only the fields the render path reads.
interface DashPlant { id?: string; status?: string }
interface DashBatch { id?: string; name?: string; status?: string }
interface DashInventoryItem { id?: string; name?: string; unit?: string; reorder_point?: number | null; quantity_on_hand?: number | null }
interface DashHarvestRow { variety_name?: string; total_kg?: number; total_revenue?: number }

// ── Design tokens ──────────────────────────────────────────────────────────────
const C = {
  bg:      'var(--color-bg)',
  card:    'var(--color-bg-card)',
  card2:   '#1A2212',
  border:  'var(--color-border)',
  amber:   'var(--color-primary)',   // accent — follows the active theme
  green:   'var(--color-accent)',    // secondary accent — follows the active theme
  text:    'var(--color-text)',
  muted:   'var(--color-text-muted)',
  faint:   'var(--color-text-faint)',
  danger:  '#F87171',
  success: '#4ADE80',
  blue:    '#60A5FA',
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const ACTION_ICON: Record<string, string> = {
  fertilize: '🌱', prune: '✂️', spray: '💨', irrigate: '💧',
  harvest: '🍋', graft: '🌿', observation: '👁', custom: '📝',
}

function relTime(iso: string) {
  const d = Date.now() - new Date(iso).getTime()
  const m = Math.floor(d / 60_000)
  if (m < 1)  return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

function tempF(c: number | undefined) {
  if (c == null) return '—'
  return `${((c * 9) / 5 + 32).toFixed(0)}°F`
}

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({
  label, value, sub, icon: Icon, accent, onClick,
}: {
  label: string; value: string | number; sub?: string
  icon: React.ElementType; accent: string; onClick?: () => void
}) {
  return (
    <div
      onClick={onClick}
      style={{
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 14,
        padding: '18px 20px', display: 'flex', alignItems: 'flex-start', gap: 14,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color 0.15s',
      }}
      onMouseEnter={e => onClick && ((e.currentTarget as HTMLElement).style.borderColor = `color-mix(in srgb, ${accent} 40%, transparent)`)}
      onMouseLeave={e => onClick && ((e.currentTarget as HTMLElement).style.borderColor = C.border)}
    >
      <div style={{
        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
        // color-mix works for both hex accents AND var(--color-primary), so the
        // icon tint follows the active theme without the `${hex}22` concat trap.
        background: `color-mix(in srgb, ${accent} 12%, transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={18} style={{ color: accent }} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11, color: C.faint, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
          {label}
        </div>
        <div style={{ fontSize: 26, fontWeight: 700, color: C.text, lineHeight: 1.2, margin: '2px 0' }}>
          {value}
        </div>
        {sub && <div style={{ fontSize: 12, color: C.muted }}>{sub}</div>}
      </div>
    </div>
  )
}

// ── Themed hero banner ──────────────────────────────────────────────────────────
// A fruity banner whose colours follow the active accent theme (a gradient of
// var(--color-primary) → var(--color-accent)) with a per-theme fruit motif on the
// right over a soft radial glow — apple is clearly red, palm teal, watermelon pink, etc.

const THEME_MOTIF: Record<string, { emoji: string; tagline: string }> = {
  mango:      { emoji: '🥭', tagline: 'Every tree, every harvest, every season.' },
  apple:      { emoji: '🍎', tagline: 'A fresh look at your orchard today.' },
  orange:     { emoji: '🍊', tagline: 'Sun-ripened and growing strong.' },
  watermelon: { emoji: '🍉', tagline: 'Sweet rows and summer vibes.' },
  palm:       { emoji: '🌴', tagline: 'Your tropical grove at a glance.' },
  nursery:    { emoji: '🌱', tagline: 'From seed to season — every start counts.' },
  floral:     { emoji: '🌸', tagline: 'In full bloom across the farm.' },
  farm:       { emoji: '🚜', tagline: 'Down on the farm — here is your day.' },
  peach:      { emoji: '🍑', tagline: 'Soft skies and sweet harvests.' },
  taupe:      { emoji: '🌾', tagline: 'Steady fields, season by season.' },
}

function DashboardBanner({ loading, title }: { loading?: boolean; title?: string }) {
  const { prefs } = useAccessibility()
  const m = THEME_MOTIF[prefs.theme] ?? THEME_MOTIF.mango
  const h = new Date().getHours()
  const greeting = h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'
  const today = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })
  return (
    <div style={{
      position: 'relative', overflow: 'hidden', borderRadius: 18, marginBottom: 22, minHeight: 138,
      padding: '26px 28px',
      // Left dark scrim keeps the white text readable even on the lighter themes,
      // layered over the theme's primary→accent gradient.
      background: 'linear-gradient(90deg, rgba(0,0,0,0.34), rgba(0,0,0,0.06) 58%, transparent), linear-gradient(115deg, var(--color-primary), var(--color-accent) 96%)',
      boxShadow: '0 10px 30px rgba(0,0,0,0.28)',
    }}>
      {loading && <RefreshCw size={15} style={{ position: 'absolute', top: 16, right: 18, color: 'rgba(255,255,255,0.85)', animation: 'spin 1.2s linear infinite', zIndex: 2 }} />}
      {/* Soft radial glow behind the fruit motif — subtle depth without the old spiky foliage. */}
      <div aria-hidden style={{ position: 'absolute', right: -40, top: '50%', transform: 'translateY(-50%)', width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.18), transparent 70%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', right: 36, top: '50%', transform: 'translateY(-50%)', fontSize: 76, lineHeight: 1, filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.3))', pointerEvents: 'none' }}>
        {m.emoji}
      </div>
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.85)' }}>{title ? `${title} · ${today}` : today}</div>
        <h1 style={{ fontSize: 27, fontWeight: 800, color: '#fff', margin: '5px 0 4px', fontFamily: '"Syne", system-ui, sans-serif', textShadow: '0 2px 8px rgba(0,0,0,0.25)' }}>{greeting}</h1>
        <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.92)', maxWidth: 430, lineHeight: 1.45 }}>{m.tagline}</div>
      </div>
    </div>
  )
}

// ── Section wrapper ───────────────────────────────────────────────────────────

function Section({
  title, action, actionLabel, children,
}: {
  title: string; action?: () => void; actionLabel?: string; children: React.ReactNode
}) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '14px 18px', borderBottom: `1px solid ${C.border}`,
      }}>
        <h2 style={{ fontSize: 13, fontWeight: 700, color: C.text, margin: 0 }}>{title}</h2>
        {action && (
          <button onClick={action} style={{
            background: 'none', border: 'none', color: C.amber, fontSize: 12,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
          }}>
            {actionLabel ?? 'View all'} <ChevronRight size={12} />
          </button>
        )}
      </div>
      {children}
    </div>
  )
}

// ── Empty state ───────────────────────────────────────────────────────────────

function Empty({ icon, msg }: { icon: string; msg: string }) {
  return (
    <div style={{ padding: '32px 18px', textAlign: 'center' }}>
      <div style={{ fontSize: 28, marginBottom: 8 }}>{icon}</div>
      <p style={{ fontSize: 13, color: C.faint, margin: 0 }}>{msg}</p>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function DashboardPage() {
  const navigate = useNavigate()
  const { tenant } = useAuthStore()

  const results = useQueries({
    queries: [
      { queryKey: ['plants'],                   queryFn: () => plantsApi.list(),                   staleTime: 60_000 },
      { queryKey: ['harvest-summary-variety'],   queryFn: () => harvestsApi.summaryByVariety(),     staleTime: 60_000 },
      { queryKey: ['propagation-batches'],       queryFn: () => propagationApi.listBatches(),       staleTime: 60_000 },
      { queryKey: ['weather-current'],           queryFn: () => weatherApi.current(),               staleTime: 30_000 },
      { queryKey: ['inventory'],                 queryFn: () => inventoryApi.list(),                staleTime: 60_000 },
      { queryKey: ['care-overdue'],              queryFn: () => careApi.overdue(),                  staleTime: 30_000 },
      { queryKey: ['care-upcoming'],             queryFn: () => careApi.upcoming(7),               staleTime: 30_000 },
      { queryKey: ['care-recent'],               queryFn: () => careApi.recentActivity(15),         staleTime: 30_000 },
    ],
  })

  const [plants, harvestSummary, batches, weather, inventory, overdue, upcoming, recent] = results

  // Derived values — all safely fall back to 0 / [] if endpoint not yet live.
  // asArray() guards every list the render path calls array methods on: API
  // bodies vary in shape ([] vs {overdue:[…]} vs {events:[…]}), and a single
  // endpoint returning an object where we expected an array used to throw
  // "X.slice is not a function" and black-screen the ENTIRE dashboard. Now a
  // mismatched shape degrades to an empty list for that one widget instead.
  const asArray = <T = Record<string, unknown>>(v: unknown, key?: string): T[] => {
    if (Array.isArray(v)) return v as T[]
    if (key) {
      const inner = (v as Record<string, unknown> | null | undefined)?.[key]
      if (Array.isArray(inner)) return inner as T[]
    }
    return []
  }

  const plantCount     = (plants.data?.data as { total?: number } | undefined)?.total ?? 0
  const plantList      = asArray<DashPlant>(plants.data?.data, 'plants')
  const harvestRows    = asArray<DashHarvestRow>(harvestSummary.data?.data)
  const batchList      = asArray<DashBatch>(batches.data?.data, 'batches')
  const activeBatches  = batchList.filter((b) => b.status === 'active')
  const weatherNow     = weather.data?.data
  const inventoryItems = asArray<DashInventoryItem>(inventory.data?.data, 'items')
  const lowStock       = inventoryItems.filter((i) => i.reorder_point != null && i.quantity_on_hand != null && i.quantity_on_hand <= i.reorder_point)
  const overdueList    = asArray<CareEvent>(overdue.data?.data, 'overdue')
  const upcomingList   = asArray<CareEvent>(upcoming.data?.data, 'upcoming')
  const recentList     = asArray<CareEvent>(recent.data?.data, 'events')

  const totalHarvestKg = harvestRows.reduce((sum: number, r) => sum + (r.total_kg ?? 0), 0)
  const producingPlants = plantList.filter((p) => p.status === 'producing').length

  const isLoading = results.some(r => r.isLoading)

  return (
    <main id="main-content" style={{ padding: '28px 32px', minHeight: '100%', background: C.bg }}>

      {/* ── Themed hero banner (replaces the plain header) ─────────────────── */}
      <DashboardBanner loading={isLoading} title={tenant?.name} />

      {/* ── Stat row ──────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
        <StatCard
          label="Plants tracked" value={plantCount}
          sub={producingPlants > 0 ? `${producingPlants} producing` : 'trees & palms'}
          icon={Leaf} accent={C.success}
          onClick={() => navigate('/plants')}
        />
        <StatCard
          label="Total harvest" value={totalHarvestKg > 0 ? `${totalHarvestKg.toFixed(0)} kg` : '—'}
          sub={harvestRows.length > 0 ? `${harvestRows.length} varieties` : 'no harvests yet'}
          icon={BarChart2} accent={C.amber}
          onClick={() => navigate('/harvest')}
        />
        <StatCard
          label="Overdue care" value={overdueList.length}
          sub={overdueList.length > 0 ? 'needs attention' : 'all caught up'}
          icon={AlertTriangle} accent={overdueList.length > 0 ? C.danger : C.success}
          onClick={() => navigate('/activity')}
        />
        <StatCard
          label="Temperature" value={tempF(weatherNow?.temp_c)}
          sub={weatherNow?.humidity_pct != null ? `${weatherNow.humidity_pct}% humidity` : 'weather station'}
          icon={CloudSun} accent={C.blue}
          onClick={() => navigate('/weather')}
        />
      </div>

      {/* ── Second stat row ────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 24 }}>
        <StatCard
          label="Active propagation" value={activeBatches.length}
          sub={activeBatches.length > 0 ? activeBatches.slice(0, 2).map((b) => b.name).join(', ') : 'no active batches'}
          icon={Sprout} accent="#A78BFA"
          onClick={() => navigate('/propagation')}
        />
        <StatCard
          label="Low stock items" value={lowStock.length}
          sub={lowStock.length > 0 ? lowStock.slice(0, 2).map((i) => i.name).join(', ') : 'inventory OK'}
          icon={Package} accent={lowStock.length > 0 ? C.amber : C.success}
          onClick={() => navigate('/inventory')}
        />
        <StatCard
          label="Upcoming care (7d)" value={upcomingList.length}
          sub={upcomingList.length > 0 ? 'tasks due this week' : 'nothing scheduled'}
          icon={Clock} accent={C.blue}
          onClick={() => navigate('/activity')}
        />
      </div>

      {/* ── Main content grid ─────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>

        {/* Overdue care */}
        <Section title="⚠ Overdue Care" action={() => navigate('/activity')} actionLabel="Log care">
          {overdueList.length === 0 ? (
            <Empty icon="✅" msg="Nothing overdue — great work!" />
          ) : (
            <div>
              {overdueList.slice(0, 8).map((ev: CareEvent, i: number) => (
                <div
                  key={ev.id ?? i}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '11px 18px', borderBottom: i < Math.min(overdueList.length, 8) - 1 ? `1px solid ${C.border}` : 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <span style={{ fontSize: 16, flexShrink: 0 }}>{ACTION_ICON[ev.action] ?? '📝'}</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: C.text, fontWeight: 500, textTransform: 'capitalize' }}>
                        {ev.action.replace(/_/g, ' ')}
                      </div>
                      <div style={{ fontSize: 11, color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {ev.plant_label ?? ev.zone_name ?? 'Farm-wide'}
                      </div>
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: C.danger, flexShrink: 0, marginLeft: 8 }}>
                    {ev.due_date ? `Due ${new Date(ev.due_date).toLocaleDateString([], { month: 'short', day: 'numeric' })}` : 'overdue'}
                  </div>
                </div>
              ))}
              {overdueList.length > 8 && (
                <div style={{ padding: '10px 18px', fontSize: 12, color: C.amber, cursor: 'pointer' }} onClick={() => navigate('/activity')}>
                  +{overdueList.length - 8} more overdue →
                </div>
              )}
            </div>
          )}
        </Section>

        {/* Upcoming this week */}
        <Section title="📅 Upcoming This Week" action={() => navigate('/activity')}>
          {upcomingList.length === 0 ? (
            <Empty icon="🌿" msg="No care tasks scheduled for the next 7 days." />
          ) : (
            <div>
              {upcomingList.slice(0, 8).map((ev: CareEvent, i: number) => (
                <div
                  key={ev.id ?? i}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '11px 18px', borderBottom: i < Math.min(upcomingList.length, 8) - 1 ? `1px solid ${C.border}` : 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <span style={{ fontSize: 16, flexShrink: 0 }}>{ACTION_ICON[ev.action] ?? '📝'}</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: C.text, fontWeight: 500, textTransform: 'capitalize' }}>
                        {ev.action.replace(/_/g, ' ')}
                      </div>
                      <div style={{ fontSize: 11, color: C.muted }}>
                        {ev.plant_label ?? ev.zone_name ?? 'Farm-wide'}
                      </div>
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: C.muted, flexShrink: 0, marginLeft: 8 }}>
                    {ev.due_date ? new Date(ev.due_date).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }) : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      {/* ── Bottom row ────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

        {/* Harvest by variety */}
        <Section title="🍋 Harvest by Variety" action={() => navigate('/harvest')}>
          {harvestRows.length === 0 ? (
            <Empty icon="📦" msg="No harvest data yet. Record your first harvest to see summaries." />
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {['Variety', 'Total kg', 'Revenue'].map(h => (
                    <th key={h} style={{
                      textAlign: h === 'Variety' ? 'left' : 'right',
                      padding: '8px 18px', fontSize: 10, color: C.faint,
                      textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {harvestRows.slice(0, 8).map((row, i: number) => (
                  <tr key={row.variety_name} style={{ borderBottom: i < Math.min(harvestRows.length, 8) - 1 ? `1px solid ${C.border}` : 'none' }}>
                    <td style={{ padding: '10px 18px', fontSize: 13, color: C.text, fontWeight: 500 }}>{row.variety_name}</td>
                    <td style={{ padding: '10px 18px', fontSize: 13, color: C.muted, textAlign: 'right' }}>
                      {(row.total_kg ?? 0).toFixed(1)} kg
                    </td>
                    <td style={{ padding: '10px 18px', fontSize: 13, color: C.success, fontWeight: 600, textAlign: 'right' }}>
                      {(row.total_revenue ?? 0) > 0 ? `$${(row.total_revenue ?? 0).toFixed(0)}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>

        {/* Recent activity */}
        <Section title="🕐 Recent Activity" action={() => navigate('/activity')}>
          {recentList.length === 0 ? (
            <Empty icon="📋" msg="No care events logged yet. Head to Activity to log your first." />
          ) : (
            <div>
              {recentList.slice(0, 8).map((ev: CareEvent, i: number) => (
                <div
                  key={ev.id ?? i}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 18px', borderBottom: i < Math.min(recentList.length, 8) - 1 ? `1px solid ${C.border}` : 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <span style={{ fontSize: 15, flexShrink: 0 }}>{ACTION_ICON[ev.action] ?? '📝'}</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: C.text, fontWeight: 500, textTransform: 'capitalize' }}>
                        {ev.action.replace(/_/g, ' ')}
                      </div>
                      <div style={{ fontSize: 11, color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {ev.plant_label ?? ev.zone_name ?? 'Farm-wide'}
                        {ev.performed_by ? ` · ${ev.performed_by}` : ''}
                      </div>
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: C.faint, flexShrink: 0, marginLeft: 8 }}>
                    {ev.occurred_at ? relTime(ev.occurred_at) : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      {/* ── Weather + irrigation quick glance ─────────────────────────────── */}
      {(weatherNow || lowStock.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginTop: 20 }}>
          {weatherNow && (
            <div
              style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px 20px', cursor: 'pointer' }}
              onClick={() => navigate('/weather')}
            >
              <div style={{ fontSize: 11, color: C.faint, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Weather Now</div>
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                {[
                  { label: '🌡 Temp',    value: tempF(weatherNow.temp_c)                              },
                  { label: '💧 Humidity', value: weatherNow.humidity_pct != null ? `${weatherNow.humidity_pct}%` : '—' },
                  { label: '🌧 Precip',   value: weatherNow.precip_mm != null ? `${weatherNow.precip_mm} mm` : '—'     },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <div style={{ fontSize: 11, color: C.faint }}>{label}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {lowStock.length > 0 && (
            <div
              style={{ background: C.card, border: `1px solid color-mix(in srgb, var(--color-primary) 30%, transparent)`, borderRadius: 14, padding: '16px 20px', cursor: 'pointer' }}
              onClick={() => navigate('/inventory')}
            >
              <div style={{ fontSize: 11, color: C.amber, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, fontWeight: 700 }}>
                ⚠ Low Stock
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {lowStock.slice(0, 4).map((item) => (
                  <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: C.text }}>{item.name}</span>
                    <span style={{ color: C.danger, fontWeight: 600 }}>{item.quantity_on_hand} {item.unit}</span>
                  </div>
                ))}
                {lowStock.length > 4 && (
                  <div style={{ fontSize: 12, color: C.amber }}>+{lowStock.length - 4} more</div>
                )}
              </div>
            </div>
          )}

          {/* Quick irrigation link */}
          <div
            style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px 20px', cursor: 'pointer', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
            onClick={() => navigate('/irrigation')}
          >
            <div style={{ fontSize: 11, color: C.faint, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Irrigation</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Droplets size={28} style={{ color: C.blue, opacity: 0.8 }} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>Manage zones</div>
                <div style={{ fontSize: 12, color: C.faint }}>Run zones, view schedules</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>
    </main>
  )
}
